import { useState } from 'react'
import { BASE_URL } from '../apiConfig';
import { toast } from 'sonner';
import { useAuth } from "../context/AuthProvider";
import { useLanguage } from "../context/LanguageProvider";
import { 
  X, Package, Calendar, Phone, MapPin, ArrowUpRight, 
  TrendingUp, Ban, AlertCircle, User
} from "lucide-react"
import { statuses } from "../cors/statusMap"
import { LiaUserSlashSolid } from "react-icons/lia";

export default function DuplicateOrders({ onClose, isOpen, HistoryOrders, client, fetchOrders, page }) {
  const { t } = useLanguage();
  const [showBlockModal, setShowBlockModal] = useState(false)
  const [blockReasonDetails, setBlockReasonDetails] = useState('')
  const [loading, setLoading] = useState(false)
  const { token , role, roles } = useAuth();
  const getStatusColor = (status) => statuses[status]?.color || "bg-gray-100 text-gray-800 border-gray-200"
  const getStatusText = (status) => statuses[status]?.text || status
  const getStatusIcon = (status, iconSize = 14) => {
    const Icon = statuses[status]?.icon
    return Icon ? <Icon size={iconSize} className="mr-1" /> : null
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    })
  }


  const handleBlockClick = () => {
    setShowBlockModal(true)
  }

  const handleBlockSubmit = async (e) => {
    e.preventDefault()

    setLoading(true)
    try {
      const response = await fetch(`${BASE_URL}/api/black-list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: client.id,
          reason: blockReasonDetails,
        }),
      })

      const data = await response.json()

      if (data.success) {
        toast.success(t('cors_duplicate_orders.client_blocked'))
        setShowBlockModal(false);
        setBlockReasonDetails('');
        fetchOrders(page);
        onClose();
      } else {
        toast.error(t('cors_duplicate_orders.error') + ' ' + data.message)
      }
    } catch (error) {
      console.error('Error blocking client:', error)
      toast.error(t('cors_duplicate_orders.error_occurred'))
    } finally {
      setLoading(false)
    }
  }

  const handleUnblockClick = async () => {
  
    try {
      const response = await fetch(`${BASE_URL}/api/black-list/${client.phone}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })
  
      const data = await response.json()
  
      if (data.success) {
        toast.success(t('cors_duplicate_orders.client_unblocked'))
        fetchOrders(page)
        onClose()
      } else {
        toast.error(data.message || t('cors_duplicate_orders.unblock_error'))
      }
    } catch (error) {
      console.error(error)
      toast.error(t('cors_duplicate_orders.error_occurred'))
    } 
  }
  

  if (!isOpen) return null


  return (
    <>
      {/* Block Modal */}
      {showBlockModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-[95%] mx-4 overflow-hidden">
            {/* Modal Header */}
            
            <div className="bg-gradient-to-br from-red-600 to-red-800 text-white p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl -mr-20 -mt-20"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 backdrop-blur-md p-2 rounded-xl border border-white/30">
                      <LiaUserSlashSolid className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold tracking-tight text-white">{t('cors_duplicate_orders.block_client')}</h2>
                      <p className="text-red-100 text-xs mt-1">
                        {t('cors_duplicate_orders.add_blacklist')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowBlockModal(false)}
                    className="p-2 cursor-pointer hover:bg-white/20 rounded-lg transition-all duration-200"
                    disabled={loading}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
            {/* Client Info Summary */}
            <div className="px-5 pt-5 border-b border-gray-100 text-left">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-50 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">
                    {client.name || t('cors_duplicate_orders.client')}
                  </h3>
                  <p className="text-xs text-gray-600">
                    {client.phone ? `${t('cors_duplicate_orders.tel')}: ${client.phone}` : t('cors_duplicate_orders.phone_unavailable')}
                  </p>
                </div>
              </div>

            </div>

            {/* Reason Form */}
            <form onSubmit={handleBlockSubmit} className="p-5 space-y-4">

              {/* Reason Details */}
              <div className='text-left'>
                  <label className="block text-xs text-gray-700 mb-2">
                    {t('cors_duplicate_orders.reason')}
                  </label>
                <textarea
                  value={blockReasonDetails}
                  onChange={(e) => setBlockReasonDetails(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-xs h-24 resize-none"
                  placeholder={t('cors_duplicate_orders.block_reason_placeholder')}
                  disabled={loading}
                />
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowBlockModal(false)}
                  className="flex-1 py-2 px-3 border border-gray-300 rounded-md text-xs cursor-pointer font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
                  disabled={loading}
                >
                  {t('cors_duplicate_orders.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-2 px-3 bg-red-700 text-white text-xs cursor-pointer rounded-md hover:bg-red-800 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {t('cors_duplicate_orders.processing')}
                    </>
                  ) : (
                    <>
                      <Ban className="w-3 h-3" />
                      {t('cors_duplicate_orders.block')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main Duplicate Orders Modal */}
      <div
        className="fixed inset-0 z-[99] flex items-center justify-center bg-black/50 backdrop-blur-md"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-5xl w-[95%] max-h-[90vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-[var(--color-primary-600)] via-[var(--color-primary-500)] to-[var(--color-primary-400)] text-white p-5 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full blur-3xl -mr-20 -mt-20"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-white/15 backdrop-blur-md p-4 rounded-xl border border-white/20 hover:bg-white/20 transition-colors duration-200">
                    <Package className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold tracking-tight text-white">{t('cors_duplicate_orders.order_history')}</h2>
                    <p className="text-slate-200 text-xs mt-1 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      {HistoryOrders?.length || 0} {t('cors_duplicate_orders.orders')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 cursor-pointer hover:bg-white/15 rounded-lg transition-all duration-200 hover:scale-110"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto flex-1 bg-gradient-to-b from-gray-50/50 to-white">
            {HistoryOrders && HistoryOrders.length > 0 ? (
              <>
                <table className="w-full">
                  <thead className="sticky top-0 bg-white border-b border-gray-200 z-10">
                    <tr className="hover:bg-gray-50/50 transition-colors">
                      <th className="px-6 py-5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        {t('cors_duplicate_orders.code')}
                      </th>
                      <th className="px-2 py-5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        {t('cors_duplicate_orders.affiliate')}
                      </th>
                      <th className="px-2 py-5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        {t('cors_duplicate_orders.client')}
                      </th>
                      <th className="px-2 py-5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        {t('cors_duplicate_orders.address')}
                      </th>
                      <th className="px-2 py-5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        {t('cors_duplicate_orders.products')}
                      </th>
                      <th className="px-2 py-5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        {t('cors_duplicate_orders.amount')}
                      </th>
                      <th className="px-2 py-5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        {t('cors_duplicate_orders.status')}
                      </th>
                      <th className="px-2 py-5 text-cener text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        {t('cors_duplicate_orders.date')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {HistoryOrders.map((order, index) => (
                      <tr
                        key={order.id}
                        className="hover:bg-gradient-to-r hover:from-[#059c8a]/5 hover:to-transparent transition-all duration-200 group"
                      >
                        <td className="py-4 text-left">
                          <span className="font-semibold text-gray-900 flex items-center text-xs gap-2 group-hover:text-[#059c8a] transition-colors">
                            <ArrowUpRight className="h-4 w-4 text-[var(--color-primary-500)] opacity-0 group-hover:opacity-100 transition-opacity" />
                            {order.code}
                          </span>
                        </td>
                        <td className="py-4 text-left px-2 text-xs">
                         {order.name_store}
                        </td>
                        <td className="py-4 text-left px-2">
                          <div className="flex items-center gap-2 text-xs">
                            <User className="h-3.5 w-3.5 text-gray-400" />
                            <span className="font-medium text-gray-900">{order.client.name}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-gray-600 text-xs">
                            <Phone className="h-3.5 w-3.5 text-gray-400" />
                            {order.client.phone}
                          </div>
                        </td>
                        <td className="py-4 text-left px-2">
                          <div className="flex items-center gap-2 text-gray-600 text-xs">
                            <MapPin className="h-4 w-4 text-gray-400" />
                            <span>
                              {order.client.city},
                            </span>
                            <span className="text-[10px]">
                              {order.client.address}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 text-left px-2">
                          {order.products && order.products.length > 0 ? (
                            <ul className="space-y-1">
                              {order.products.map((product, index) => (
                                <li key={index} className="flex items-center gap-2">
                                  <Package className="w-3.5 h-3.5 text-gray-500" />
                                  <span className='text-[11px] font-[400]'>{product}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-gray-400">{t('cors_duplicate_orders.no_products')}</span>
                          )}
                        </td>
                      
                        <td className="py-4 text-left px-2">
                          <span className="font-bold text-xs text-[#059c8a] py-1 rounded-lg inline-block">
                            {Number.parseFloat(order.total).toFixed(2)} MAD
                          </span>
                        </td>
                        <td className="py-4 text-left px-2">
                          <div
                            className={`px-1.5 py-0.5 rounded-full border font-medium text-[11px] flex items-center w-fit gap-1 ${getStatusColor(order.status)}`}
                          >
                            {getStatusIcon(order.status, 13)}
                            {getStatusText(order.status)}
                          </div>
                        </td>
                        <td className="py-4 text-left px-2">
                          <div className="flex items-center gap-2 text-gray-600 text-xs">
                            <Calendar className="h-4 w-4 text-gray-400" />
                              <div className="text-[11px] text-gray-900">{formatDate(order.created_at)}</div>
                              <div className="text-[11px] text-gray-800">{formatTime(order.created_at)}</div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <div className="flex items-center justify-center h-64 flex-col">
                <div className="text-center">
                  <div className="bg-gradient-to-br from-[#059c8a]/10 to-[var(--color-primary-50)] rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                    <Package className="h-10 w-10 text-[var(--color-primary-500)]" />
                  </div>
                  <p className="text-gray-900 text-lg font-semibold">{t('cors_duplicate_orders.no_orders_found')}</p>
                  <p className="text-gray-600 text-xs mt-1">{t('cors_duplicate_orders.orders_will_appear')}</p>
                </div>
              </div>
            )}
            {
              (role === 1 || (role === 2 && roles.add_blacklist === 1)) &&
              <div className="sticky bottom-0 bg-gradient-to-t from-white via-white to-transparent p-3">
                <div className="flex justify-end">
                  {
                    client.black_listed === false ?
                      <button 
                        onClick={handleBlockClick}
                        className="px-3 cursor-pointer font-medium text-[11px] py-2 bg-red-700 text-white rounded-md hover:bg-red-800 transition flex items-center gap-1"
                      >
                        <LiaUserSlashSolid className="w-4 h-4" />
                        {t('cors_duplicate_orders.block')}
                      </button>
                      :
                      <button 
                        onClick={handleUnblockClick}
                        className="px-3 cursor-pointer font-medium text-[11px] py-2 bg-red-700 text-white rounded-md hover:bg-red-800 transition flex items-center gap-1"
                      >
                        <Ban className="w-3 h-3" />
                        {t('cors_duplicate_orders.unblock')}
                      </button>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </>
  )
}